import { useState } from 'react';
import { Copy, Globe } from 'lucide-react';

import { useAssetMatchesQuery, useOffPlatformMutation, type OffPlatform } from '@/client/api/assets/duplicates';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/** Where else this photo appears: on the platform (pHash, free) and on the web (billed). */
const AssetProvenancePanel = ({ asset }: { asset: Asset }) => {
  const { data, isLoading } = useAssetMatchesQuery(asset.asid, 6);
  const web = useOffPlatformMutation();
  const { hasPermission: canPay, isSuperAdmin } = useHasPermission('asset.run.paid');
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<OffPlatform | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (asset.kind && asset.kind !== 'image') return null;

  return (
    <div>
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Copy className="h-4 w-4" /> Copies</h4>
      {isLoading ? <Skeleton className="h-10" /> : !data?.phash ? (
        <p className="text-sm text-gray-500">Not hashed yet, so copies cannot be found. Measure it from Jobs or Duplicates.</p>
      ) : !data.matches.length ? (
        <p className="text-sm text-gray-500">No other photo on the platform matches.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {data.matches.slice(0, 20).map((m) => (
            <li key={m.asid} className="flex gap-2">
              <a className="text-blue-600 hover:underline" href={`/admin/assets?q=${m.asid}`} target="_blank" rel="noreferrer">#{m.asid}</a>
              <span className="text-gray-500">{m.eid ? `listing #${m.eid}` : ''} {m.owner ?? ''}</span>
              <span className={m.cross_owner ? 'font-medium text-red-700' : 'text-gray-500'}>{m.cross_owner ? 'other seller' : 'same seller'}</span>
              <span className="text-gray-400">{m.distance === 0 ? 'identical' : `Δ${m.distance}`}</span>
            </li>
          ))}
        </ul>
      )}
      {(canPay || isSuperAdmin) && (
        <div className="mt-3">
          <Button size="sm" variant="outline" disabled={web.isPending || asset.private === 1} onClick={() => setConfirm(true)}>
            <Globe className="mr-1.5 h-3.5 w-3.5" /> Search the web <span className="ml-1 text-[10px] text-purple-700">billed</span>
          </Button>
          {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
          {result && (
            <div className="mt-2 text-xs">
              {!result.checked ? <p className="text-gray-500">Not checked: {result.reason}</p> : !result.matches.length ? (
                <p className="text-gray-500">Not found elsewhere on the web.</p>
              ) : (
                <ul className="space-y-1">
                  {result.matches.slice(0, 15).map((m, i) => (
                    <li key={i}><a className="text-blue-600 hover:underline" href={m.url} target="_blank" rel="noreferrer noopener">{m.title || m.source || m.url}</a></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Search the web for this photo?</AlertDialogTitle>
            <AlertDialogDescription>One billed reverse-image search (Google Lens through ff-api-external). Use it when a photo looks lifted from another site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async (e) => {
              e.preventDefault();
              setConfirm(false);
              setErr(null);
              try { setResult(await web.mutateAsync(asset.asid!)); } catch (x) { setErr(getApiErrorMessage(x, 'Web search failed')); }
            }}>Search (billed)</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AssetProvenancePanel;
